import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Instagram, Mail, MessageCircle, Trophy, ArrowRight, Users, Heart, Loader2, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SignupForm } from '../components/SignupForm';
import { submitToGoogleSheets } from '../utils/googleSheets';
export function LandingPage() {
  const [activePhoto, setActivePhoto] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Compact CTA form state
  const [ctaInput1, setCtaInput1] = useState('');
  const [ctaStatus1, setCtaStatus1] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [ctaInput2, setCtaInput2] = useState('');
  const [ctaStatus2, setCtaStatus2] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  // Hero signup state
  const [heroInput, setHeroInput] = useState('');
  const [heroStatus, setHeroStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleCtaSubmit = async (e: React.FormEvent, input: string, setStatus: React.Dispatch<React.SetStateAction<'idle' | 'loading' | 'success' | 'error'>>, setInput: React.Dispatch<React.SetStateAction<string>>, source: string) => {
    e.preventDefault();
    if (!input.trim()) return;
    setStatus('loading');
    // Validate input - must be email or 10+ digit phone
    const isValid = input.includes('@') || input.replace(/\D/g, '').length >= 10;
    if (!isValid) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }
    // Submit to Google Sheets
    const result = await submitToGoogleSheets(input, source);
    if (result.success) {
      setStatus('success');
      setInput('');
      setTimeout(() => setStatus('idle'), 3000);
    } else {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };
  // Handle scroll for navbar shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Photo carousel interval
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhoto(prev => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  const photos = [{
    src: 'https://cdn.magicpatterns.com/uploads/qzVmcvaAQwpTaozY461XCu/IMG_0002.jpg',
    alt: 'Flag football action'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/m3HQx3iQwJiTrAHZ6UBt3t/IMG_5587.jpg',
    alt: 'Basketball game'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oBMvpJffbnHdogX9is9jdQ/IMG_5610.jpg',
    alt: 'Soccer match'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/67cxANWbk6fcbczqRJXb5m/IMG_5631.jpg',
    alt: 'Community sports event'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/t9yrpwnNQoTrwpsvgC7BeK/IMG_5635.jpg',
    alt: 'Team preparation'
  }];
  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-grotesk selection:bg-primary/20 selection:text-primary" data-id="element-16">
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2' : 'bg-white py-3'}`} data-id="element-17">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between" data-id="element-18">
          {/* Logo */}
          <div className="w-32 md:w-40 transition-transform hover:scale-105 duration-300 cursor-pointer" onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })} data-id="element-19">
            <img src="https://cdn.magicpatterns.com/uploads/7BLGhA4eXEXNnfhMUdMU4i/Google_Gemini_Edited_Image.png" alt="SD Jewish Sports Group Logo" className="w-full h-auto" data-id="element-20" />
          </div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-21">
            <button onClick={() => scrollToSection('winter-cup')} className="text-navy font-medium hover:text-primary transition-colors" data-id="element-22">
              Winter Cup
            </button>
            <button onClick={() => scrollToSection('schedule')} className="text-navy font-medium hover:text-primary transition-colors" data-id="element-23">
              Schedule
            </button>
            <button onClick={() => scrollToSection('signup')} className="bg-primary hover:bg-blue-600 text-white px-6 py-2.5 rounded-full font-bold transition-all shadow-sm hover:shadow-md active:scale-95" data-id="element-24">
              Join
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-navy p-2" onClick={() => setIsMenuOpen(!isMenuOpen)} data-id="element-25">
            {isMenuOpen ? <X className="w-6 h-6" data-id="element-26" /> : <Menu className="w-6 h-6" data-id="element-27" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-28">
          {isMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-29">
              <div className="flex flex-col p-4 gap-4" data-id="element-30">
                <button onClick={() => scrollToSection('winter-cup')} className="text-navy font-medium py-2 hover:text-primary text-left" data-id="element-31">
                  Winter Cup
                </button>
                <button onClick={() => scrollToSection('schedule')} className="text-navy font-medium py-2 hover:text-primary text-left" data-id="element-32">
                  Schedule
                </button>
                <button onClick={() => scrollToSection('signup')} className="bg-primary text-white px-6 py-3 rounded-full font-bold text-center" data-id="element-33">
                  Join Community
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <header className="relative w-full bg-white overflow-hidden pt-28 pb-8 md:pt-36 md:pb-12" data-id="element-34">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-35">
          <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] bg-blue-100/40 rounded-full blur-3xl opacity-60 animate-pulse" data-id="element-36" />
          <div className="absolute top-[10%] -right-[10%] w-[50%] h-[70%] bg-indigo-50/60 rounded-full blur-3xl opacity-60" data-id="element-37" />
          <div className="absolute bottom-0 left-1/3 w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" data-id="element-38" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 z-10" data-id="element-39">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16" data-id="element-40">
            {/* Left Content */}
            <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-41">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} data-id="element-42">
                <h1 className="text-4xl md:text-6xl font-bold text-navy leading-tight mb-6" data-id="element-43">
                  Building Jewish Community Through{' '}
                  <span className="text-primary relative inline-block" data-id="element-44">
                    Sport
                    <svg className="absolute w-full h-3 bottom-1 left-0 text-primary/20 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-45">
                      <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" data-id="element-46" />
                    </svg>
                  </span>
                </h1>

                {/* Primary Actions - Contact Buttons */}
                <div className="mb-8" data-id="element-47">
                  <div className="flex flex-wrap justify-center md:justify-start gap-3" data-id="element-48">
                    <a href="https://instagram.com/sdjewishsports" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-navy hover:bg-navy/90 text-white px-5 py-3 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 font-medium" data-id="element-49">
                      <Instagram className="w-5 h-5" data-id="element-50" />
                      <span data-id="element-51">Instagram</span>
                    </a>

                    <a href="https://wa.me/18586686292" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-navy hover:bg-navy/90 text-white px-5 py-3 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 font-medium" data-id="element-52">
                      <MessageCircle className="w-5 h-5" data-id="element-53" />
                      <span data-id="element-54">WhatsApp</span>
                    </a>

                    <a href="mailto:info@sdjewishsports.com" className="flex items-center gap-2 bg-navy hover:bg-navy/90 text-white px-5 py-3 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 font-medium" data-id="element-55">
                      <Mail className="w-5 h-5" data-id="element-56" />
                      <span data-id="element-57">Email</span>
                    </a>
                  </div>
                </div>

                {/* Secondary Action - Newsletter Signup */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100" data-id="element-58">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3" data-id="element-59">
                    Stay Updated
                  </p>

                  {heroStatus === 'success' ? <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 text-green-700" data-id="element-60">
                      <div className="bg-green-100 p-1.5 rounded-full" data-id="element-61">
                        <ArrowRight className="w-4 h-4" data-id="element-62" />
                      </div>
                      <span className="font-medium text-sm" data-id="element-63">
                        You're on the list! We'll be in touch.
                      </span>
                    </div> : <form onSubmit={e => handleCtaSubmit(e, heroInput, setHeroStatus, setHeroInput, 'Hero Form (Top)')} className="relative" data-id="element-64">
                      <div className="flex flex-col sm:flex-row gap-3" data-id="element-65">
                        <input type="text" value={heroInput} onChange={e => {
                      setHeroInput(e.target.value);
                      if (heroStatus === 'error') setHeroStatus('idle');
                    }} placeholder="Email or phone" disabled={heroStatus === 'loading'} className={`flex-grow px-5 py-3.5 rounded-xl border-2 outline-none transition-all placeholder:text-gray-400 text-base ${heroStatus === 'error' ? 'border-red-300 bg-red-50 focus:border-red-500' : 'border-gray-200 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20'}`} data-id="element-66" />
                        <button type="submit" disabled={heroStatus === 'loading' || !heroInput.trim()} className="bg-primary hover:bg-blue-600 text-white px-6 py-3.5 rounded-xl font-bold transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap text-base sm:min-w-[120px]" data-id="element-67">
                          {heroStatus === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-68" /> : <>
                              Join List
                              <ArrowRight className="w-4 h-4" data-id="element-69" />
                            </>}
                        </button>
                      </div>
                      {heroStatus === 'error' && <p className="text-red-500 text-xs font-medium mt-2 animate-in slide-in-from-top-1" data-id="element-70">
                          Please enter a valid email or phone number
                        </p>}
                    </form>}
                  <p className="text-xs text-gray-500 mt-2.5" data-id="element-71">
                    Join 100+ members in San Diego
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Content - Photo Carousel */}
            <div className="w-full md:w-1/2" data-id="element-72">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="relative aspect-[4/3] md:aspect-square rounded-3xl overflow-hidden shadow-2xl border-4 border-white" data-id="element-73">
                <AnimatePresence mode="wait" data-id="element-74">
                  <motion.img key={activePhoto} src={photos[activePhoto].src} alt={photos[activePhoto].alt} initial={{
                  opacity: 0,
                  scale: 1.05
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.5
                }} className="absolute inset-0 w-full h-full object-cover" data-id="element-75" />
                </AnimatePresence>

                {/* Carousel Indicators */}
                <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-10" data-id="element-76">
                  {photos.map((_, index) => <button key={index} onClick={() => setActivePhoto(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activePhoto ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/80'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-77" />)}
                </div>

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent pointer-events-none" data-id="element-78" />
              </motion.div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow" data-id="element-79">
        {/* Subtle CTA 1 */}

        {/* Featured Event: Winter Cup */}
        <section id="winter-cup" className="py-12 px-4 bg-navy text-white relative overflow-hidden" data-id="element-80">
          {/* Background decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-81">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary rounded-full blur-3xl" data-id="element-82"></div>
            <div className="absolute top-1/2 right-0 w-64 h-64 bg-blue-400 rounded-full blur-3xl" data-id="element-83"></div>
          </div>

          <div className="max-w-4xl mx-auto relative z-10" data-id="element-84">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl" data-id="element-85">
              <div className="flex-1 text-center md:text-left" data-id="element-86">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-bold uppercase tracking-wider mb-6 border border-primary/20" data-id="element-87">
                  <Trophy className="w-4 h-4" data-id="element-88" />
                  Upcoming Event
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-89">
                  🏀 Winter Cup <span className="text-primary" data-id="element-90">2026</span>
                </h2>
                <p className="text-xl text-gray-300 mb-8 font-light" data-id="element-91">
                  Join us for the premier 3v3 basketball tournament of the
                  season.
                </p>

                <div className="space-y-4 mb-8" data-id="element-92">
                  <div className="flex items-center justify-center md:justify-start gap-3 text-lg" data-id="element-93">
                    <Calendar className="w-6 h-6 text-primary" data-id="element-94" />
                    <span className="font-medium" data-id="element-95">January 4, 2026</span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-3 text-lg" data-id="element-96">
                    <Clock className="w-6 h-6 text-primary" data-id="element-97" />
                    <span className="font-medium" data-id="element-98">11:00 AM - 1:00 PM</span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-3 text-lg" data-id="element-99">
                    <Heart className="w-6 h-6 text-primary" data-id="element-100" />
                    <span className="font-medium" data-id="element-101">
                      Open to all, no experience required
                    </span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-3 text-lg" data-id="element-102">
                    <Users className="w-6 h-6 text-primary" data-id="element-103" />
                    <span className="font-medium" data-id="element-104">
                      Sign up solo or as a team
                    </span>
                  </div>
                </div>

                <a href="https://bit.ly/wintercup2026" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-blue-600 text-white text-lg font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-primary/50 hover:-translate-y-1 w-full md:w-auto" data-id="element-105">
                  Register Now
                  <ArrowRight className="w-5 h-5" data-id="element-106" />
                </a>
              </div>

              {/* Visual graphic for basketball */}
              <div className="w-full md:w-1/3 flex justify-center" data-id="element-107">
                <div className="relative w-48 h-48 md:w-64 md:h-64" data-id="element-108">
                  {/* Overlapping basketball action photos */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-109">
                    <div className="relative w-full h-full" data-id="element-110">
                      {/* Photo 1 - Back */}
                      <div className={`absolute top-4 left-0 w-32 h-32 md:w-40 md:h-40 rotate-[-8deg] shadow-xl rounded-lg overflow-hidden border-4 border-white transition-all duration-700 ${activePhoto === 0 ? 'z-30 scale-110' : 'z-10 scale-100 opacity-70'}`} data-id="element-111">
                        <img src="https://cdn.magicpatterns.com/uploads/ggq4n7hvxqaUvEBS12Rh2c/50AFF75E-45E0-435F-882D-5B1036B1C2C8.jpg" alt="Basketball action" className="w-full h-full object-cover" data-id="element-112" />
                      </div>
                      {/* Photo 2 - Middle */}
                      <div className={`absolute top-8 right-0 w-32 h-32 md:w-40 md:h-40 rotate-[6deg] shadow-xl rounded-lg overflow-hidden border-4 border-white transition-all duration-700 ${activePhoto === 1 ? 'z-30 scale-110' : 'z-10 scale-100 opacity-70'}`} data-id="element-113">
                        <img src="https://cdn.magicpatterns.com/uploads/a9BziCuu6WF41mRjARvfm4/87501D1F-2D62-4F01-A82D-2E8C1F01A282.jpg" alt="Basketball game" className="w-full h-full object-cover" data-id="element-114" />
                      </div>
                      {/* Photo 3 - Front */}
                      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-36 h-36 md:w-44 md:h-44 rotate-[-3deg] shadow-2xl rounded-lg overflow-hidden border-4 border-white transition-all duration-700 ${activePhoto === 2 ? 'z-30 scale-110' : 'z-20 scale-100 opacity-70'}`} data-id="element-115">
                        <img src="https://cdn.magicpatterns.com/uploads/j5fuuNwiu3mfuwFeh95U7L/B87F5985-95B7-4EF9-90C6-D6620093C527.jpg" alt="Basketball tournament" className="w-full h-full object-cover" data-id="element-116" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Subtle CTA 2 */}
        <div className="py-6 px-4 bg-white border-y border-gray-100" data-id="element-117">
          <div className="max-w-2xl mx-auto" data-id="element-118">
            {ctaStatus2 === 'success' ? <p className="text-center text-sm text-green-600 font-medium" data-id="element-119">
                ✓ Thanks! You're on the list.
              </p> : <form onSubmit={e => handleCtaSubmit(e, ctaInput2, setCtaStatus2, setCtaInput2, 'CTA Form (Middle)')} className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-120">
                <label htmlFor="cta-input-2" className="text-sm text-gray-600 font-medium whitespace-nowrap" data-id="element-121">
                  Never miss an event →
                </label>
                <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-122">
                  <input id="cta-input-2" type="text" value={ctaInput2} onChange={e => {
                setCtaInput2(e.target.value);
                if (ctaStatus2 === 'error') setCtaStatus2('idle');
              }} placeholder="Email or phone" disabled={ctaStatus2 === 'loading'} className={`flex-1 sm:w-64 px-4 py-2 text-sm rounded-full border outline-none transition-all ${ctaStatus2 === 'error' ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20'}`} data-id="element-123" />
                  <button type="submit" disabled={ctaStatus2 === 'loading' || !ctaInput2.trim()} className="bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap" data-id="element-124">
                    {ctaStatus2 === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-125" /> : <>
                        Join
                        <ArrowRight className="w-3 h-3" data-id="element-126" />
                      </>}
                  </button>
                </div>
                {ctaStatus2 === 'error' && <p className="text-red-500 text-xs font-medium mt-2 animate-in slide-in-from-top-1" data-id="element-127">
                    Please enter a valid email or phone number
                  </p>}
              </form>}
          </div>
        </div>

        {/* 2026 Schedule Section */}
        <section id="schedule" className="py-20 px-4 bg-gray-50" data-id="element-128">
          <div className="max-w-5xl mx-auto" data-id="element-129">
            {/* Header */}
            <div className="text-center mb-0 overflow-hidden rounded-t-2xl shadow-lg" data-id="element-130">
              <div className="bg-primary py-6 px-4" data-id="element-131">
                <h2 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-wide" data-id="element-132">
                  2026 Schedule
                </h2>
              </div>
              <div className="bg-navy py-3 px-4" data-id="element-133">
                <p className="text-white font-medium uppercase tracking-wider text-sm md:text-base" data-id="element-134">
                  Monthly Sunday Events Throughout 2026
                </p>
              </div>
            </div>

            {/* Schedule Grid */}
            <div className="shadow-xl rounded-b-2xl overflow-hidden" data-id="element-135">
              {/* Winter Cup */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="grid grid-cols-1 md:grid-cols-12 bg-sky-200 border-b border-white/20 min-h-[140px]" data-id="element-136">
                <div className="md:col-span-4 p-6 md:p-8 flex items-center justify-center md:justify-start" data-id="element-137">
                  <h3 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight" data-id="element-138">
                    Winter Cup
                  </h3>
                </div>
                <div className="md:col-span-8 p-4 md:p-6 flex flex-col justify-center gap-3" data-id="element-139">
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-140">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-141">
                      JAN 4
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-142">
                      🏀 Basketball
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-143">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-144">
                      FEB 1
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-145">
                      ⚽ Soccer & 🏈 Flag Football
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Spring Open */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="grid grid-cols-1 md:grid-cols-12 bg-emerald-200 border-b border-white/20 min-h-[140px]" data-id="element-146">
                <div className="md:col-span-4 p-6 md:p-8 flex items-center justify-center md:justify-start" data-id="element-147">
                  <h3 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight" data-id="element-148">
                    Spring Open
                  </h3>
                </div>
                <div className="md:col-span-8 p-4 md:p-6 flex flex-col justify-center gap-3" data-id="element-149">
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-150">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-151">
                      MAR 1
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-152">
                      🏀 Basketball
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-153">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-154">
                      APR 5
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-155">
                      ⚽ Soccer & 🏈 Flag Football
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* The Shavuot Classic */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="grid grid-cols-1 md:grid-cols-12 bg-amber-200 border-b border-white/20 min-h-[140px]" data-id="element-156">
                <div className="md:col-span-4 p-6 md:p-8 flex items-center justify-center md:justify-start" data-id="element-157">
                  <h3 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight leading-none" data-id="element-158">
                    The Shavuot <br data-id="element-159" />
                    Classic
                  </h3>
                </div>
                <div className="md:col-span-8 p-4 md:p-6 flex flex-col justify-center gap-3" data-id="element-160">
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-161">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-162">
                      MAY 3
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-163">
                      🏀 Basketball{' '}
                      <span className="ml-2 text-primary normal-case italic font-semibold" data-id="element-164">
                        (Qualifiers)
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-165">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-166">
                      JUN 7
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-167">
                      🏀 Basketball{' '}
                      <span className="ml-2 text-primary normal-case italic font-semibold" data-id="element-168">
                        (Playoffs)
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Summer Series */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="grid grid-cols-1 md:grid-cols-12 bg-orange-200 border-b border-white/20 min-h-[140px]" data-id="element-169">
                <div className="md:col-span-4 p-6 md:p-8 flex items-center justify-center md:justify-start" data-id="element-170">
                  <h3 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight" data-id="element-171">
                    Summer Series
                  </h3>
                </div>
                <div className="md:col-span-8 p-4 md:p-6 flex flex-col justify-center gap-3" data-id="element-172">
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-173">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-174">
                      JUL 12
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-175">
                      🏐 Beach Volleyball
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-176">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-177">
                      AUG 2
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-178">
                      ⚽ Soccer & 🏈 Flag Football
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-179">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-180">
                      AUG 30
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-181">
                      🏀 Basketball
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Fall Games */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="grid grid-cols-1 md:grid-cols-12 bg-orange-500 min-h-[140px]" data-id="element-182">
                <div className="md:col-span-4 p-6 md:p-8 flex items-center justify-center md:justify-start" data-id="element-183">
                  <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight" data-id="element-184">
                    Fall Games
                  </h3>
                </div>
                <div className="md:col-span-8 p-4 md:p-6 flex flex-col justify-center gap-3" data-id="element-185">
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-white rounded-lg overflow-hidden shadow-sm" data-id="element-186">
                    <div className="bg-white px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-navy border-b sm:border-b-0 sm:border-r border-gray-100" data-id="element-187">
                      OCT 11
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-navy uppercase" data-id="element-188">
                      ⚽ Soccer & 🏈 Flag Football
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-0 sm:gap-4 bg-primary rounded-lg overflow-hidden shadow-md transform scale-[1.02]" data-id="element-189">
                    <div className="bg-primary px-6 py-3 min-w-[120px] flex items-center justify-center font-bold text-white border-b sm:border-b-0 sm:border-r border-white/20" data-id="element-190">
                      NOV 27
                    </div>
                    <div className="px-6 py-3 flex items-center font-bold text-white uppercase tracking-wide" data-id="element-191">
                      SD Jewish Sports Day
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Signup Section */}
        <section className="py-20 px-4 bg-white" id="signup" data-id="element-192">
          <div className="max-w-3xl mx-auto text-center" data-id="element-193">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-194">
              Join the Community
            </h2>
            <p className="text-lg text-gray-600 mb-10 max-w-xl mx-auto" data-id="element-195">
              Don't miss out on upcoming leagues, tournaments, and community
              events. Sign up for updates today.
            </p>
            <SignupForm data-id="element-196" />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-navy text-white py-12 px-4 border-t border-white/10" data-id="element-197">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-198">
          <div className="text-center md:text-left" data-id="element-199">
            <h3 className="text-2xl font-bold mb-2" data-id="element-200">SD Jewish Sports Group</h3>
            <p className="text-gray-400 text-sm" data-id="element-201">
              © 2026 All rights reserved.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6" data-id="element-202">
            <a href="https://instagram.com/sdjewishsports" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 px-5 py-3 rounded-full transition-all duration-300 border border-white/5 hover:border-white/20" data-id="element-203">
              <Instagram className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" data-id="element-204" />
              <span className="font-medium" data-id="element-205">@sdjewishsports</span>
            </a>

            <a href="mailto:info@sdjewishsports.com" className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 px-5 py-3 rounded-full transition-all duration-300 border border-white/5 hover:border-white/20" data-id="element-206">
              <Mail className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" data-id="element-207" />
              <span className="font-medium" data-id="element-208">info@sdjewishsports.com</span>
            </a>

            <a href="https://wa.me/18586686292" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 px-5 py-3 rounded-full transition-all duration-300 border border-white/5 hover:border-white/20" data-id="element-209">
              <MessageCircle className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" data-id="element-210" />
              <span className="font-medium" data-id="element-211">(858) 668-6292</span>
            </a>
          </div>
        </div>
      </footer>
    </div>;
}